import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { FolderOpen, Upload, X, FileText, Link, Music, RotateCcw, GitBranch } from 'lucide-react';
import { uploadApi } from '../lib/api';
import { toast } from 'sonner';

const PHASE_TYPES = [
  { value: 'charla', label: 'Charla' },
  { value: 'pausa', label: 'Pausa' },
  { value: 'intro', label: 'Introducción' },
  { value: 'qa', label: 'Q&A' },
  { value: 'video', label: 'Video' },
  { value: 'networking', label: 'Networking' },
  { value: 'otro', label: 'Otro' },
];

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
];

const CONTENT_TYPES = [
  { value: 'image',    label: 'Imagen' },
  { value: 'video',    label: 'Video URL' },
  { value: 'document', label: 'Documento' },
  { value: 'audio',    label: 'Audio' },
];

// Collect unique {content_type, content_url} already used in other phases for a given screen_id
const getExistingContent = (phases, currentPhaseId, screenId) => {
  const seen = new Set();
  const results = [];
  for (const phase of phases) {
    if (currentPhaseId && String(phase.id) === String(currentPhaseId)) continue;
    const val = phase.screen_values?.[screenId];
    if (!val) continue;
    const content_type = typeof val === 'object' ? (val.content_type || 'image') : 'image';
    const content_url  = typeof val === 'object' ? (val.content_url  || '')      : (val || '');
    if (!content_url) continue;
    const key = `${content_type}::${content_url}`;
    if (!seen.has(key)) {
      seen.add(key);
      results.push({ content_type, content_url });
    }
  }
  return results;
};

export const PhaseDialog = ({ open, onOpenChange, phase, parentId = null, parentPhase = null, speakers, screens = [], phases = [], onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration_minutes: '5',
    duration_seconds: '0',
    phase_type: 'charla',
    speaker_ids: [],
    color: '#3B82F6',
    notes: '',
    section: 'show',
  });
  // screenValues: { [screen_id]: { content_type: 'image'|'video'|'document'|'audio', content_url: '' } }
  const [screenValues, setScreenValues] = useState({});
  const [uploading, setUploading] = useState({});
  // Which resource slot is showing the reuse picker
  const [reuseOpen, setReuseOpen] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (phase) {
      const mins = Math.floor(phase.duration_seconds / 60);
      const secs = phase.duration_seconds % 60;
      let speakerIds = phase.speaker_ids || [];
      if (speakerIds.length === 0 && phase.speaker_id) {
        speakerIds = [phase.speaker_id];
      }
      setFormData({
        name: phase.name,
        description: phase.description || '',
        duration_minutes: mins.toString(),
        duration_seconds: secs.toString(),
        phase_type: phase.phase_type,
        speaker_ids: speakerIds,
        color: phase.color,
        notes: phase.notes || '',
        section: phase.section,
      });
      // Normalise existing screen_values
      const normalized = {};
      for (const [sid, val] of Object.entries(phase.screen_values || {})) {
        if (typeof val === 'object' && val !== null) {
          normalized[sid] = { content_type: val.content_type || 'image', content_url: val.content_url || '' };
        } else {
          normalized[sid] = { content_type: 'image', content_url: val || '' };
        }
      }
      setScreenValues(normalized);
    } else {
      setFormData({
        name: '',
        description: '',
        duration_minutes: '5',
        duration_seconds: '0',
        phase_type: 'charla',
        speaker_ids: [],
        color: '#3B82F6',
        notes: '',
        section: 'show',
      });
      setScreenValues({});
    }
    setReuseOpen({});
  }, [phase, open]);

  const handleSpeakerToggle = (speakerId) => {
    setFormData(prev => ({
      ...prev,
      speaker_ids: prev.speaker_ids.includes(speakerId)
        ? prev.speaker_ids.filter(id => id !== speakerId)
        : [...prev.speaker_ids, speakerId]
    }));
  };

  const getScreenVal = (screenId) =>
    screenValues[screenId] || { content_type: 'image', content_url: '' };

  const setScreenContentType = (screenId, content_type) => {
    setScreenValues(prev => ({
      ...prev,
      [screenId]: { content_type, content_url: '' }
    }));
    setReuseOpen(prev => ({ ...prev, [screenId]: false }));
  };

  const setScreenContentUrl = (screenId, content_url) => {
    setScreenValues(prev => ({
      ...prev,
      [screenId]: { ...getScreenVal(screenId), content_url }
    }));
  };

  const applyExisting = (screenId, item) => {
    setScreenValues(prev => ({
      ...prev,
      [screenId]: { content_type: item.content_type, content_url: item.content_url }
    }));
    setReuseOpen(prev => ({ ...prev, [screenId]: false }));
  };

  const handleFileUpload = async (screenId, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [screenId]: true }));
    try {
      const res = await uploadApi.upload(file);
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setScreenValues(prev => ({
        ...prev,
        [screenId]: { ...getScreenVal(screenId), content_url: url }
      }));
    } catch {
      toast.error('Error al subir archivo');
    } finally {
      setUploading(prev => ({ ...prev, [screenId]: false }));
    }
  };

  const handleRemoveContent = (screenId) => {
    setScreenValues(prev => ({
      ...prev,
      [screenId]: { ...getScreenVal(screenId), content_url: '' }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const duration_seconds = parseInt(formData.duration_minutes) * 60 + parseInt(formData.duration_seconds || 0);
    // Include ALL screens (even empty ones) so the backend can delete cleared entries.
    // If content_url is empty, the backend will delete that screen_value row.
    const allScreenValues = {};
    for (const screen of screens) {
      allScreenValues[screen.id] = screenValues[screen.id] || { content_type: 'image', content_url: '' };
    }
    onSubmit({
      name: formData.name,
      description: formData.description,
      duration_seconds,
      phase_type: formData.phase_type,
      speaker_ids: formData.speaker_ids,
      color: formData.color,
      notes: formData.notes,
      section: formData.section,
      ...(parentId ? { parent_id: parentId } : {}),
    }, allScreenValues);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {phase ? 'Editar fase' : parentId ? 'Nueva subfase' : 'Nueva fase'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {parentPhase && (
            <div className="flex items-center gap-2 text-xs bg-muted/50 border border-border rounded px-3 py-2">
              <GitBranch className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">Subfase de:</span>
              <span className="font-medium truncate">{parentPhase.name}</span>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="phase-name">Nombre *</Label>
            <Input
              id="phase-name"
              placeholder="Ej: Keynote de apertura"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              data-testid="phase-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phase-description">Descripción</Label>
            <Input
              id="phase-description"
              placeholder="Descripción breve..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Duración</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="999"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                  className="w-16"
                  data-testid="phase-duration-min"
                />
                <span className="text-xs text-muted-foreground">min</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.duration_seconds}
                  onChange={(e) => setFormData({ ...formData, duration_seconds: e.target.value })}
                  className="w-16"
                  data-testid="phase-duration-sec"
                />
                <span className="text-xs text-muted-foreground">seg</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={formData.phase_type}
                onValueChange={(value) => setFormData({ ...formData, phase_type: value })}
              >
                <SelectTrigger data-testid="phase-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHASE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* On Stage - Multiple speakers */}
          <div className="space-y-2">
            <Label>On Stage (ponentes en escena)</Label>
            {speakers.length > 0 ? (
              <ScrollArea className="h-28 border rounded-md p-2">
                <div className="space-y-2">
                  {speakers.map(speaker => (
                    <div key={speaker.id} className="flex items-center gap-3">
                      <Checkbox
                        id={`speaker-${speaker.id}`}
                        checked={formData.speaker_ids.includes(speaker.id)}
                        onCheckedChange={() => handleSpeakerToggle(speaker.id)}
                      />
                      <label
                        htmlFor={`speaker-${speaker.id}`}
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium overflow-hidden">
                          {speaker.avatar_url ? (
                            <img src={speaker.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            speaker.initials
                          )}
                        </div>
                        <span className="text-sm">{speaker.name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-sm text-muted-foreground">No hay ponentes. Créalos primero.</p>
            )}
          </div>

          {/* Recursos dinámicos */}
          {screens.length > 0 && (
            <div className="space-y-3">
              <Label className="flex items-center gap-1">
                <FolderOpen className="w-3.5 h-3.5" /> Recursos
              </Label>
              <div className="space-y-4">
                {screens.map(screen => {
                  const val = getScreenVal(screen.id);
                  const existing = getExistingContent(phases, phase?.id, screen.id);
                  const isReuseOpen = reuseOpen[screen.id];

                  return (
                    <div key={screen.id} className="border border-border rounded-md p-3 space-y-2.5">
                      {/* Header row: name + type selector */}
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-sm font-medium">{screen.name}</span>
                        <div className="flex gap-1">
                          {CONTENT_TYPES.map(ct => (
                            <button
                              key={ct.value}
                              type="button"
                              onClick={() => setScreenContentType(screen.id, ct.value)}
                              className={`text-xs px-2 py-1 rounded border transition-colors ${
                                val.content_type === ct.value
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                              }`}
                            >
                              {ct.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Content input */}
                      {val.content_type === 'video' ? (
                        <div className="flex items-center gap-2">
                          <Link className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <Input
                            placeholder="URL del video..."
                            value={val.content_url}
                            onChange={(e) => setScreenContentUrl(screen.id, e.target.value)}
                            className="text-sm h-8"
                          />
                          {val.content_url && (
                            <button type="button" onClick={() => handleRemoveContent(screen.id)}>
                              <X className="w-4 h-4 text-destructive" />
                            </button>
                          )}
                        </div>
                      ) : val.content_url ? (
                        // Has content: image / document / audio preview
                        <div className="flex items-center gap-2">
                          {val.content_type === 'image' ? (
                            <img
                              src={val.content_url}
                              alt={screen.name}
                              className="h-16 w-28 object-cover rounded border border-border flex-shrink-0"
                            />
                          ) : val.content_type === 'audio' ? (
                            <div className="flex items-center gap-2 px-3 py-2 rounded border border-border bg-muted/30 text-sm flex-1">
                              <Music className="w-4 h-4 text-primary flex-shrink-0" />
                              <span className="truncate text-xs">
                                {val.content_url.split('/').pop()}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-2 rounded border border-border bg-muted/30 text-sm flex-1">
                              <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                              <span className="truncate text-xs">
                                {val.content_url.split('/').pop()}
                              </span>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveContent(screen.id)}
                            className="w-5 h-5 rounded-full bg-destructive flex items-center justify-center flex-shrink-0"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        // No content: upload button
                        <button
                          type="button"
                          onClick={() => fileInputRefs.current[screen.id]?.click()}
                          disabled={uploading[screen.id]}
                          className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          {uploading[screen.id]
                            ? 'Subiendo...'
                            : val.content_type === 'document'
                              ? 'Subir PDF / PPT'
                              : val.content_type === 'audio'
                                ? 'Subir audio'
                                : 'Subir imagen'}
                        </button>
                      )}

                      <input
                        ref={el => { fileInputRefs.current[screen.id] = el; }}
                        type="file"
                        accept={
                          val.content_type === 'document'
                            ? '.pdf,.ppt,.pptx,.doc,.docx'
                            : val.content_type === 'audio'
                              ? 'audio/*'
                              : 'image/*'
                        }
                        className="hidden"
                        onChange={(e) => handleFileUpload(screen.id, e.target.files?.[0])}
                      />

                      {/* Reuse existing content from other phases */}
                      {existing.length > 0 && (
                        <div>
                          <button
                            type="button"
                            onClick={() => setReuseOpen(prev => ({ ...prev, [screen.id]: !prev[screen.id] }))}
                            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Reutilizar de otra fase ({existing.length})
                          </button>

                          {isReuseOpen && (
                            <div className="mt-2 flex flex-wrap gap-2 p-2 rounded-md bg-muted/30 border border-border">
                              {existing.map((item, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => applyExisting(screen.id, item)}
                                  className="group relative rounded overflow-hidden border border-border hover:border-primary transition-colors"
                                  title={item.content_url.split('/').pop()}
                                >
                                  {item.content_type === 'image' ? (
                                    <img
                                      src={item.content_url}
                                      alt=""
                                      className="h-12 w-20 object-cover group-hover:opacity-80 transition-opacity"
                                    />
                                  ) : item.content_type === 'audio' ? (
                                    <div className="h-12 w-20 flex flex-col items-center justify-center bg-muted gap-1 group-hover:opacity-80">
                                      <Music className="w-5 h-5 text-primary" />
                                      <span className="text-[9px] text-muted-foreground truncate max-w-[70px] px-1">
                                        {item.content_url.split('/').pop()}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="h-12 w-20 flex flex-col items-center justify-center bg-muted gap-1 group-hover:opacity-80">
                                      <FileText className="w-5 h-5 text-primary" />
                                      <span className="text-[9px] text-muted-foreground truncate max-w-[70px] px-1">
                                        {item.content_url.split('/').pop()}
                                      </span>
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {!parentId && (
            <div className="space-y-2">
              <Label>Sección</Label>
              <Select
                value={formData.section}
                onValueChange={(value) => setFormData({ ...formData, section: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre-show">Pre-show</SelectItem>
                  <SelectItem value="show">Show</SelectItem>
                  <SelectItem value="post-show">Post-show</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-1.5">
                {COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`w-6 h-6 rounded-full transition-transform hover:scale-110 ${
                      formData.color === color ? 'ring-2 ring-offset-2 ring-primary' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phase-notes">Notas para el operador</Label>
            <Textarea
              id="phase-notes"
              placeholder="Notas internas..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="phase-submit-btn">
              {phase ? 'Guardar cambios' : 'Crear fase'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PhaseDialog;
